import api from "../services/api";
import { captacionApiBase, resolveCaptacionMediaUrl } from "../services/captacionUpload";

const RAW_URL_RE = /\.(dng|x-adobe-dng|cr2|cr3|nef|nrw|arw|orf|rw2|raf|raw|pef|srw)(\?|$)/i;
const RAW_MIME_RE = /image\/(x-adobe-dng|x-canon|x-nikon|x-sony|x-olympus|x-panasonic|x-fuji|x-pentax)|\/dng\b/i;

/** RAW/DNG: el navegador no puede pintarlos en &lt;img&gt; (no forzar descarga de 20+ MB). */
export function esFotoRawSinPreview(foto) {
  if (!foto) return false;
  const blob = [
    foto.url,
    foto.url_display,
    foto.url_thumb,
    foto.nombre_archivo,
    foto.preview,
    foto.mime_type,
    foto.mimetype,
    foto.content_type,
  ]
    .filter(Boolean)
    .join(" ");
  return RAW_URL_RE.test(blob) || RAW_MIME_RE.test(blob);
}

/**
 * URL para miniatura en grid (ligera; no la foto completa de 15 MB).
 * @param {{ soloThumb?: boolean }} opts  soloThumb=true → no caer a full (parrilla Para revisar).
 */
export function urlFotoGrid(foto, opts = {}) {
  if (!foto) return "";
  if (esFotoRawSinPreview(foto)) return "";
  const soloThumb = opts.soloThumb === true;
  const raw = soloThumb
    ? (foto.thumbPreview || foto.url_thumb || "")
    : (
      foto.thumbPreview
      || foto.url_thumb
      || foto.url_display
      || foto.preview
      || foto.url
      || ""
    );
  if (!raw) return "";
  if (raw.startsWith("blob:") || raw.startsWith("http")) return raw;
  return resolveCaptacionMediaUrl(raw);
}

/** ¿La foto ya tiene miniatura usable en parrilla estricta? */
export function fotoTieneThumbGrid(foto) {
  if (!foto || esFotoRawSinPreview(foto)) return true;
  return !!(foto.thumbPreview || foto.url_thumb);
}

/** URL para lightbox / descarga individual (calidad final si existe). */
export function urlFotoCompleta(foto) {
  if (!foto) return "";
  if (esFotoRawSinPreview(foto)) return "";
  const raw = foto.url_display || foto.url || foto.preview || "";
  if (!raw) return "";
  if (raw.startsWith("blob:") || raw.startsWith("http")) return raw;
  return resolveCaptacionMediaUrl(raw);
}

export function mapFotoCaptacionDesdeApi(f) {
  const thumb = f.url_thumb || null;
  const display = f.url_display || f.url;
  const esRaw = esFotoRawSinPreview(f);
  const thumbResolved = (!esRaw && thumb) ? resolveCaptacionMediaUrl(thumb) : null;
  return {
    id: f.id,
    url: f.url,
    url_thumb: thumb,
    url_display: f.url_display || null,
    nombre_archivo: f.nombre_archivo || null,
    mime_type: f.mime_type || f.mimetype || null,
    es_raw: esRaw,
    preview: esRaw ? null : (thumbResolved || resolveCaptacionMediaUrl(display)),
    thumbPreview: thumbResolved,
    existente: true,
    tipo: "publica",
    descripcion_es: f.descripcion_es || "",
    marco_autoenhance: f.marco_autoenhance !== false,
    usar_web: f.usar_web !== false,
    en_seleccion_marco: f.en_seleccion_marco === true,
    subidaEstado: "ok",
    estado: f.estado || null,
    numero_archivo: f.numero_archivo ?? null,
  };
}

export function mapFotoPropiedadDesdeApi(f) {
  const thumb = f.url_thumb || null;
  const display = f.url_display || f.url;
  const yaEditada = fotoYaEditadaPorMarco({ ...f, url: display });
  const thumbResolved = thumb
    ? (String(thumb).startsWith("http") ? thumb : resolveCaptacionMediaUrl(thumb))
    : null;
  const preview = thumbResolved
    || (String(display || "").startsWith("http") ? display : resolveCaptacionMediaUrl(display));
  return {
    id: f.id,
    url: f.url,
    url_thumb: thumb,
    url_display: f.url_display || null,
    preview,
    thumbPreview: thumbResolved,
    tipo: f.tipo || "publica",
    descripcion_es: f.descripcion_es || "",
    existente: true,
    marco_autoenhance: yaEditada ? false : (f.marco_autoenhance !== false),
    estado: f.estado || null,
    numero_archivo: f.numero_archivo || null,
  };
}

/** Foto ya procesada por Marco (no volver a retocar). */
export function fotoYaEditadaPorMarco(foto) {
  if (!foto) return false;
  return (
    foto.estado === "editada"
    || foto.numero_archivo != null
    || String(foto.url || "").includes("/editadas/")
    || String(foto.preview || "").includes("/editadas/")
  );
}

export function nombreArchivoFotoDescarga(foto, index, referencia) {
  const ref = String(referencia || "fotos").replace(/[^\w.-]+/g, "_").slice(0, 40);
  const base = foto.numero_archivo || foto.id || `foto-${index + 1}`;
  const extMatch = String(foto.url || foto.preview || "").match(/\.(jpe?g|png|webp)(\?|$)/i);
  const ext = extMatch ? extMatch[1].toLowerCase().replace("jpeg", "jpg") : "jpg";
  return `${ref}_${base}.${ext}`;
}

async function fetchBlobDesdeFoto(foto) {
  if (foto.file instanceof Blob) return foto.file;

  const url = foto.preview || foto.url;
  if (!url) throw new Error("sin_url");

  if (url.startsWith("blob:")) {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.blob();
  }

  if (foto.existente && foto.id) {
    const r = await api.get(`/fotos/${foto.id}/descargar`, { responseType: "blob" });
    return r.data;
  }

  const resp = await fetch(url, { mode: "cors" });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.blob();
}

function esZipValido(buffer) {
  const u8 = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  return u8.length >= 4 && u8[0] === 0x50 && u8[1] === 0x4B;
}

function descargarUrlNativa(url) {
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;width:0;height:0;border:0;visibility:hidden";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = url;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    try { iframe.remove(); } catch { /* ignore */ }
  }, 300_000);
}

function descargarBlobLocal(blob, nombreArchivo) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreArchivo;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
}

/**
 * Descarga ZIP desde el servidor sin pasar el binario por JavaScript.
 * El navegador guarda un único .zip (evita límites del proxy de Vercel en fetch).
 */
function descargarZipServidorNativo(propiedadId, nombreZip) {
  const token = localStorage.getItem("token");
  const params = new URLSearchParams();
  if (token) params.set("token", token);
  params.set("filename", nombreZip);
  const url = `${captacionApiBase()}/propiedades/${propiedadId}/fotos/descargar-zip?${params}`;
  descargarUrlNativa(url);
}

export function nombreZipFotosPropiedad(referencia) {
  const ref = String(referencia || "fotos").replace(/[^\w.-]+/g, "_").slice(0, 40);
  return `${ref}_fotos_email.zip`;
}

export async function descargarFotoMultimedia(foto, index, referencia) {
  const blob = await fetchBlobDesdeFoto(foto);
  descargarBlobLocal(blob, nombreArchivoFotoDescarga(foto, index, referencia));
}

export async function descargarTodasFotosZip(fotos, referencia, propiedadId) {
  return descargarFotosZip(fotos, referencia, propiedadId);
}

/** Descarga un subconjunto de fotos (p. ej. selección) en ZIP. */
export async function descargarFotosZip(fotos, referencia, propiedadId, opts = {}) {
  const lista = Array.isArray(fotos) ? fotos : [];
  if (!lista.length) return null;
  const nombreZip = opts.nombreZip || nombreZipFotosPropiedad(referencia);

  if (propiedadId && lista.length === (opts.totalFotos ?? lista.length)) {
    descargarZipServidorNativo(propiedadId, nombreZip);
    return nombreZip;
  }

  return generarZipLocal(lista, referencia, nombreZip);
}

async function generarZipLocal(fotos, referencia, nombreZip) {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  let ok = 0;
  for (let i = 0; i < fotos.length; i++) {
    const foto = fotos[i];
    if (!foto.preview && !foto.url && !foto.file) continue;
    try {
      const blob = await fetchBlobDesdeFoto(foto);
      zip.file(nombreArchivoFotoDescarga(foto, i, referencia), blob);
      ok += 1;
    } catch {
      /* omitir foto con error */
    }
  }
  if (!ok) throw new Error("No se pudo descargar ninguna foto");
  const out = await zip.generateAsync({
    type: "arraybuffer",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
  if (!esZipValido(out)) throw new Error("No se pudo crear el ZIP");
  descargarBlobLocal(new Blob([out], { type: "application/zip" }), nombreZip);
  return nombreZip;
}
